/**
 * ConsentBanner — opt-in analytics controls; not a legal compliance certification.
 * Analytics (GA4) only loads after explicit "Accept". Declining keeps
 * the site fully functional with essential cookies only.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { useLocation } from 'react-router-dom';

const NAVY = '#1E3F70';
const KEY = 'ucw_consent';

export const ConsentBanner = () => {
  const { pathname } = useLocation();
  const privateConsole = pathname.startsWith('/ucw-sys-');
  const hostWorkspace = pathname === '/hosts' || pathname.startsWith('/hosts/');
  const workerWorkspace = pathname === '/worker' || pathname.startsWith('/worker/');
  const authPage = pathname === '/login' || pathname === '/signup' || pathname.endsWith('/login') || pathname.endsWith('/signup');
  const inlineBanner = hostWorkspace || workerWorkspace || authPage || pathname.startsWith('/stay/');
  const bannerRef = useRef(null);
  const [choice, setChoice] = useState(() => {
    try { return localStorage.getItem(KEY); } catch { return 'denied'; }
  });

  useEffect(() => {
    const measure = () => document.documentElement.style.setProperty('--ucw-consent-height', choice || privateConsole || inlineBanner ? '0px' : `${(bannerRef.current?.getBoundingClientRect().height || 0) + 20}px`);
    measure();
    const observer = new ResizeObserver(measure);
    if (bannerRef.current) observer.observe(bannerRef.current);
    return () => { observer.disconnect(); document.documentElement.style.removeProperty('--ucw-consent-height'); };
  }, [choice, privateConsole, inlineBanner]);

  if (choice || privateConsole) return null;

  const decide = (value) => {
    try { localStorage.setItem(KEY, value); } catch { /* private mode */ }
    if (value === 'granted' && typeof window.__grantConsent === 'function') window.__grantConsent();
    setChoice(value);
  };

  return (
    <div ref={bannerRef} className={inlineBanner ? 'relative w-full border-b bg-white' : 'fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-md z-[95]'} data-testid="consent-banner">
      <div className={inlineBanner ? 'max-w-7xl mx-auto px-5 py-4' : 'rounded-3xl bg-white p-5 shadow-2xl ring-1 ring-gray-100'}>
        <div className="text-sm font-bold" style={{ color: NAVY }}>Your privacy, your call</div>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
          We use optional analytics cookies to understand how travellers use UCWonder and improve the experience.
          Decline and we keep only anonymous, cookie-free visit counts — essential cookies (like staying signed in) always work.{' '}
          <a href="/privacy" className="underline font-semibold" data-testid="consent-privacy-link">Privacy policy</a>
        </p>
        <div className="flex flex-wrap gap-2 mt-3.5">
          <Button size="sm" onClick={() => decide('granted')} className="rounded-full text-white text-xs px-5" style={{ background: NAVY }} data-testid="consent-accept-btn">
            Accept analytics
          </Button>
          <Button size="sm" variant="outline" onClick={() => decide('denied')} className="rounded-full text-xs px-5" style={{ color: NAVY }} data-testid="consent-decline-btn">
            Essential only
          </Button>
        </div>
      </div>
    </div>
  );
};
